import React, { useState, useEffect } from 'react';
import { Button, ErrorMessage, FlexRow, Grid, Header4Text, ModalWindow, PageLoading } from 'ui-toolkit';
import { useGetWiresIncomingActualQuery, usePostMatchWiresIncomingMutation } from 'API/accounting/AccountingApi';
import { ePermissions } from 'types/administration/userAdministration/ePermissions';
import { createDateRangeFromDays } from 'utilities/accounting/createDateRangeFromDays';
import { eMatchedQueryValue } from 'types/accounting/eMatchedQueryValue';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { convertToError } from 'API/BaseApi';
import { ePayerType } from 'types/order/escrow/ePayerType';
import { eDepositCategory } from 'types/order/escrow/eDepositCategory';
import { IEscrowActualIncomingWireData } from 'types/accounting/data/IEscrowActualIncomingWireData';
import { MatchActualWireDepositTable } from './MatchActualWireDepositTable';
import { IEscrowActualIncomingWireQuery } from 'types/accounting/query/IEscrowActualIncomingWireQuery';
import { useHasPermissions } from '../../../../accessControl/hooks/useHasPermissions';

export interface IMatchingWireModalSectionProps {
  expectedWire: { amountCents: number; payerType: ePayerType; category: eDepositCategory; id: string };
  onClose: () => void;
}

export const MatchingWireModalSection: React.FC<IMatchingWireModalSectionProps> = ({ onClose, expectedWire }) => {
  const [selectedWire, setSelectedWire] = useState<IEscrowActualIncomingWireData | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const getDefaultWireQuery: () => IEscrowActualIncomingWireQuery = () => ({
    amountCents: expectedWire.amountCents,
    amountCentsMin: expectedWire.amountCents,
    completedAtDate: createDateRangeFromDays(-60),
    matched: eMatchedQueryValue.UNMATCHED,
    pageNumber: 1,
    pageSize: 25,
    sortField: 'completedAtDate',
    sortOrder: 'desc',
  });

  const [queryObject, setQueryObject] = useState(getDefaultWireQuery);

  const hasExpectedReadPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingWiresRead]);
  const hasActualReadPermission = useHasPermissions([ePermissions.EscrowActualIncomingWiresRead]);
  const hasActualEditPermission = useHasPermissions([ePermissions.EscrowActualIncomingWiresEdit]);
  const hasExpectedEditPermission = useHasPermissions([ePermissions.EscrowExpectedIncomingWiresEdit]);

  const skip = !(hasActualReadPermission && hasExpectedReadPermission);

  const {
    data: getActualWires,
    isLoading: isLoadingGetActualWires,
    error: getActualWiresError,
  } = useGetWiresIncomingActualQuery(queryObject, {
    skip,
  });

  useEffect(() => {
    const newSelectedWire = selectedId && getActualWires && getActualWires.content.find((i) => i.id === selectedId);
    if (newSelectedWire && ((selectedWire && newSelectedWire.id !== selectedWire.id) || !selectedWire)) {
      setSelectedWire(newSelectedWire);
    }
  }, [getActualWires, selectedWire, selectedId]);

  const [doMatch, { error: matchError }] = usePostMatchWiresIncomingMutation();
  const onMatch = async () => {
    if (!selectedWire || !hasActualEditPermission || !hasExpectedEditPermission) return;

    const result = await doMatch({ actualId: selectedWire.id, expectedId: expectedWire.id });

    if (checkForMutationSuccess(result, 'matching wire')) {
      onClose();
    }
  };

  const isQueryValid =
    !queryObject.amountCents || !queryObject.amountCentsMin || queryObject.amountCents >= queryObject.amountCentsMin;

  return (
    <>
      {getActualWires && (
        <ModalWindow
          title={<Header4Text>Match an Actual Wire Deposit</Header4Text>}
          onClose={onClose}
          width={`90vw`}
          footer={
            <FlexRow justifyContent={'flex-end'}>
              <Grid columnCount={2} justify-self="end" columnGap={'spacing3'}>
                <Button intent={'ghost'} onClick={onClose} data-test-id={'cancel-button'}>
                  Cancel
                </Button>
                <Button
                  intent={'primary'}
                  disabled={!selectedWire}
                  onClick={onMatch}
                  data-test-id={'wire-match-button'}
                >
                  Match
                </Button>
              </Grid>
            </FlexRow>
          }
        >
          <PageLoading isLoading={isLoadingGetActualWires}>
            {!isQueryValid && (
              <ErrorMessage
                message={`The amount of the expected deposit must be less than the remaining amount of the incoming transaction.`}
              />
            )}
            <ErrorMessage
              data-test-id={'main-error-location'}
              error={convertToError(getActualWiresError) || convertToError(matchError)}
            />
            <MatchActualWireDepositTable
              data={getActualWires}
              isQueryFetching={isLoadingGetActualWires}
              query={queryObject}
              setQuery={setQueryObject}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
            />
          </PageLoading>
        </ModalWindow>
      )}
    </>
  );
};
